import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";
import { AuthDto, AuthJuniorDto, IClub } from "@eagerdog/interfaces";

import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import { apiService } from "src/services";
import { helperService, IDogPortrait } from "../../services/helper.service";
import { localStorageService } from "src/services/local-storage.service";

import logo from "../../assets/logo.svg";
import emailIcon from "../../assets/icons/email.svg";
import keyIcon from "../../assets/icons/key.svg";

import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import { toast } from "src/components/Toast/ToastManager";

import "../../styles/Signup.scss";

interface IProps {}

const Login: React.FC<IProps> = (props) => {
  const navigate = useNavigate();

  let [email, setEmail] = useState<string>("");
  let [password, setPassword] = useState<string>("");

  const juniorClubs = [{ value: "AKC", id: "AKC" }, { value: "UKC", id: "UKC" }, { value: "CKC", id: "CKC" }, { value: "Other", id: "Other" }];
  const [isJunior, setIsJunior] = useState<boolean>(false);
  const [juniorClub, setJuniorClub] = useState<IOption>(juniorClubs[0]);
  const [juniorNumber, setJuniorNumber] = useState<string>("");

  // Get the return URL from query params to preserve for register link
  const queryParameters = new URLSearchParams(window.location.search);
  const returnUrl = queryParameters.get("r");
  
  let registerLink = "/register";
  if (returnUrl) {
    registerLink = `/register/dog-owner?r=${encodeURIComponent(returnUrl)}`;
  } else {
    // Legacy: check if URL contains event path
    let _url = window.location.href.split("/e/")[1];
    registerLink = _url ? `/register/dog-owner?r=/e/${_url}` : "/register";
  }
  
  let learnMoreLink = "https://about.eagerdog.com";
  const resetPasswordLink = '/send-code';

  const randomDog = helperService.allDogs[Math.floor(Math.random() * helperService.allDogs.length)];

  const login = () => {
    let payload = {};

    if (isJunior) {
      payload = {
        sanctioning_club: juniorClub.value,
        junior_number: juniorNumber,
        password: password,
      };
    } else {
      payload = {
        email: email,
        password: password,
      };
    }

    const promise = isJunior ? apiService.loginJunior(payload as AuthJuniorDto) : apiService.login(payload as AuthDto);

    // apiService.login(payload).then((response: any) => {
    promise.then((response: any) => {
      if (response._id) {
        const queryParameters = new URLSearchParams(window.location.search);
        const redirect = queryParameters.get("r");

        apiService.getUserCapacities().then((capacities) => {
          localStorageService.setItem(localStorageService.CAPACITIES, JSON.stringify(capacities.capacities));

          if (redirect && redirect !== "/") {
            navigate(redirect);
          } else {
            if (response.user_type === Constants.user_type.club_manager) {
              apiService.getCurrentClub().then((clubResponse) => {
                navigate("/k/" + clubResponse._id);
              });
            } else {
              navigate("/");
            }
            
          }
        }).catch((error:any) => {
          toast.show({
            title: "Error",
            content: error?.response?.data?.message || "Something went wrong, please try again later",
            duration: 10000,
            type: "fail"
          });
        });

        if (response.user_type === Constants.user_type.club_manager) {
          apiService.getCurrentClub().then((club: IClub) => {
            localStorageService.setItem(localStorageService.CLUB, JSON.stringify(club));
          });
        }
      }
    }).catch((error:any) => {
      toast.show({
        title: "Error",
        content: error?.response?.data?.message || "Could not connect to server. Please make sure the backend is running.",
        duration: 10000,
        type: "fail"
      });
    });
  }

  return (
    <div className="Login Signup">
    <Helmet>
      <title>Login</title>
      <meta name="description" content="Login to your Eager Dog account." />
      <meta name="robots" content="index, follow" />
    </Helmet>
      <div className="images">
        <div className="logo"><Link to="/"><img src={logo} alt="Eager Dog" /></Link></div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.2 }} className="dogCards">
          <div className="cardRow">
            {helperService.topDogs.map((dog:IDogPortrait, i:number) => {
              return(
                <div key={i} className="dogCard">
                  <div className="dogPortrait">
                    <img src={dog.image} alt={"Dog Portrait of " + dog.name} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="cardRow">
            {helperService.bottomDogs.map((dog:IDogPortrait, i:number) => {
              return(
                <div key={i} className="dogCard">
                  <div className="dogPortrait">
                    <img src={dog.image} alt={"Dog Portrait of " + dog.name} />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, translateY: "-300px" }} whileInView={{ opacity: 1, translateY: "-50px" }} transition={{ delay: 0.5 }} className="dogBanner">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 1 }} className="bannerInner">
            <div className="small">The Best Place</div>
            <div className="large">for your <span>Dog</span></div>
            <div className="dogWrap">
              <div className="portrait"><img src={randomDog.image} alt={"Dog Portrait of " + randomDog.name} /></div>
            </div>
            <div className="dogName"></div>
          </motion.div>
        </motion.div>
      </div>
      <div className="content">
        <div className="inner">
          <div className="logo"><img src={logo} alt="Eager Dog" /></div>
          <h1>Login</h1>
          <div className="form">
            <form onSubmit={(e) => { e.preventDefault(); login(); }}>
              <Checkbox onChange={() => { setIsJunior(!isJunior); }} value={isJunior} className="juniorCb" id="isJunior" label="Use a Junior Number" />

              {!isJunior &&
                <Input type="email" onChange={(e:any) => { setEmail(e.target.value); }} icon={emailIcon} label="Email" placeholder="Enter your email" />
              }

              {isJunior && <>
                <Dropdown label="Sanctioning club" options={juniorClubs} placeholder="" value={juniorClub} onChange={(_e, option) => { setJuniorClub(option); }} />
                <Input required onChange={(e) => { setJuniorNumber(e.target.value); }} label="Junior #" placeholder="Enter club junior number" />
              </>}

              <Input type="password" onChange={(e:any) => { setPassword(e.target.value); }} icon={keyIcon} label="Password" placeholder="Enter your password" />
              <div className="forgot">
                <Link to={resetPasswordLink}>Forgot your password?</Link>                               
              </div>
              <div className="actions">
                <button type="submit">Login</button>
              </div>
              <div className="register">Don't have an account yet? <Link to={registerLink}>Sign Up</Link></div>
              <div className="register">Want to <Link to={learnMoreLink}>learn more</Link> about Eager Dog? </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
